import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  getFakeClientUser,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { MenuItem, Loader, Icon } from '@pwc/pwc-deals-ui';
import InfiniteScroll from 'react-infinite-scroll-component';
import Button from '@pwc/pwc-deals-ui/build/common/Button';
import featureFlags from 'common/helpers/featureFlags';
import AddProjectModal from '../AddProjectModal';
import {
  useProjectsStatuses,
  getProjectStatusIcon,
} from '../../hooks/useProjectsStatuses';
import {
  getPagedProjects,
  getPagedProjectsSelector,
} from '../../../features/projects/pagedProjectsSlice';

import { projectOverviewPath } from '../../services/urlService';
import {
  resetProject,
  getProject,
} from '../../../features/project/projectSlice';
import authorizationService from '../../services/security/authorizationService';
import styles from './ProjectListSideNavButtons.module.scss';

const ProjectListSideNavButtons = ({ closeSideNav, mobile }) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClientUser = useSelector(getFakeClientUser);

  const [showModal, setShowModal] = useState(false);
  const projectsStates = useProjectsStatuses();
  const { loading, projects, pageSize, total, currentPage } = useSelector(
    getPagedProjectsSelector
  );
  const project = useSelector(getProject);
  const selectedProjectId = project && project.id;

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const ICON_SIZE = 24;

  const history = useHistory();

  const title = t('addProject');

  const hasMoreItems = total > projects.length;

  const fetchPagedProjects = (page) => {
    dispatch(getPagedProjects({ page, pageSize }));
  };

  const handleNext = () => !loading && fetchPagedProjects(currentPage + 1);

  const isExactActive = (projectId) => {
    return selectedProjectId === projectId ? 'menu-active' : '';
  };

  const handleClick = () => {
    if (featureFlags.isProjectInitiationFromCDEEnabled) {
      window.open(
        `${process.env.REACT_APP_DS_CREATE_URL}projects/new`,
        '_blank',
        'noreferrer'
      );
    } else {
      setShowModal(true);
    }
    closeSideNav();
  };

  const viewAllProjects = () => {
    history.push('/projects');
    closeSideNav();
  };

  const handleProjectClick = () => {
    dispatch(resetProject());
    closeSideNav();
  };

  const ScrollLoader = () => (
    <>
      {loading && (
        <Loader className="small" data-testid="infite-scroll-loader" />
      )}
    </>
  );

  return (
    <>
      {mobile ? (
        <div
          id="all-projects-container"
          className={styles.menuNavigationWrapper}
        >
          <div className="all-projects-title" data-testid="all-projects-title">
            {t('mobileViewAll')}&nbsp;
            <span key={process.env.REACT_APP_PLATFORM_NAME}>
              {process.env.REACT_APP_PLATFORM_NAME}
            </span>
            &nbsp;{t('mobileViewProjects')}
          </div>
          <div className="menu-navigation">
            <InfiniteScroll
              dataLength={projects.length}
              hasMore={hasMoreItems}
              next={handleNext}
              scrollableTarget="all-projects-container"
              scrollThreshold={0.75}
              loader={<ScrollLoader />}
            >
              {projects.map((currentProject) => {
                const projectStatusIcon = getProjectStatusIcon(
                  projectsStates,
                  currentProject.id
                );

                return (
                  <MenuItem
                    navTarget={projectOverviewPath(
                      currentProject.id,
                      isPwcUser
                    )}
                    icon={
                      <Icon
                        name="folder"
                        className="outline icon-grey"
                        width={`${ICON_SIZE}`}
                        height={`${ICON_SIZE}`}
                      />
                    }
                    statusIcons={
                      projectStatusIcon
                        ? [
                            <Icon
                              name={projectStatusIcon}
                              className="fill icon-black state-icon"
                            />,
                          ]
                        : []
                    }
                    title={currentProject.name}
                    onClick={handleProjectClick}
                    linkClass={isExactActive(
                      projectOverviewPath(currentProject.id, isPwcUser)
                    )}
                    key={currentProject.id}
                  />
                );
              })}
            </InfiniteScroll>
            <div className={styles.viewAllProjects}>
              <Button
                className="button-link button-theme mx-2 mr-4"
                onClick={viewAllProjects}
                type="button"
              >
                {t('viewAllProjects')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="menu-navigation">
          {authorizationService.canCreateProjects() && !isFakeClientUser && (
            <>
              <MenuItem
                navTarget=""
                icon={
                  <Icon
                    name="add"
                    className="fill icon-orange"
                    width={ICON_SIZE}
                  />
                }
                title={title}
                onClick={() => handleClick()}
                linkClass=""
              />
              <AddProjectModal
                showModal={showModal}
                handleClose={setShowModal}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ProjectListSideNavButtons;
